import React from 'react';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles(() => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    field: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 0',
            fontSize: 14,
            fontWeight: 600,
        },
    },
    searchButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 14,
        borderRadius: 8,
        padding: '6px 18px',
        backgroundColor: '#43419e',
        color: '#fff',
        boxShadow: 'none',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: '#37357f' },
        '&.Mui-disabled': { backgroundColor: '#d5d7e8', color: '#fff' },
    },
    ghostButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 14,
        borderRadius: 8,
        padding: '6px 12px',
        color: '#515f6d',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: '#eceef8' },
    },
}));

interface TableSearchBarProps {
    /** Live input value. */
    value?: string;
    onChange: (value: string) => void;
    /** Commits the term — the only thing that should hit the API. */
    onSearch: () => void;
    onClear: () => void;
    placeholder?: string;
    /** Input differs from the committed term, so there is something to submit. */
    isDirty?: boolean;
    /** A term is currently applied to the grid. */
    hasApplied?: boolean;
    width?: number | string;
}

/**
 * Search input with an explicit Search / Clear pair. Typing never queries —
 * the term is submitted on the CTA or Enter, so a page using this makes exactly
 * one request per search instead of one per keystroke.
 */
const TableSearchBar: React.FC<TableSearchBarProps> = ({
    value = '',
    onChange,
    onSearch,
    onClear,
    placeholder = 'Search…',
    isDirty = false,
    hasApplied = false,
    width = 320,
}) => {
    const styles = useStyles();

    return (
        <Box className={styles.wrapper}>
            <TextField
                variant="outlined"
                size="small"
                className={styles.field}
                style={{ width }}
                placeholder={placeholder}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        onSearch();
                    }
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon
                                fontSize="small"
                                style={{ color: '#8a92a6' }}
                            />
                        </InputAdornment>
                    ),
                    endAdornment: !!value && (
                        <InputAdornment position="end">
                            <Tooltip title="Clear search" arrow>
                                <IconButton size="small" onClick={onClear}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
            <Button
                variant="contained"
                disableElevation
                className={styles.searchButton}
                disabled={!isDirty}
                onClick={onSearch}
            >
                Search
            </Button>
            {(!!value || !!hasApplied) && (
                <Button className={styles.ghostButton} onClick={onClear}>
                    Clear
                </Button>
            )}
        </Box>
    );
};

export default TableSearchBar;
